import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Spinner } from '../Spinner'

// Revisar e publicar no portal do cliente (quinzenal ou achado). O texto publicado é uma
// cópia: editar aqui não muda o relatório interno, e refazer o interno não muda o publicado.

export function PublishModal({
  open,
  heading,
  initialTitle,
  initialContent,
  loadingDraft,
  publishing,
  error,
  done,
  onClose,
  onPublish,
}: {
  open: boolean
  heading: string
  initialTitle: string
  initialContent: string
  loadingDraft?: boolean
  publishing: boolean
  error: string | null
  done: boolean
  onClose: () => void
  onPublish: (title: string, content: string) => void
}) {
  const [title, setTitle] = useState(initialTitle)
  const [content, setContent] = useState(initialContent)
  const [preview, setPreview] = useState(false)
  useEffect(() => {
    setTitle(initialTitle)
    setContent(initialContent)
  }, [initialTitle, initialContent])

  if (!open) return null
  // No body (como o ActionQuickEditModal): dentro da página, a animação de entrada usa
  // transform e prenderia o "fixed" à página em vez da tela.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-base font-semibold text-slate-900">{heading}</h2>
          <p className="text-xs text-slate-500">
            O cliente vê exatamente este texto no portal. Revise: nada de hipótese em aberto, jargão ou assunto interno.
          </p>
        </div>
        <div className="flex-1 space-y-3 overflow-auto px-5 py-4">
          {loadingDraft ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Spinner className="h-4 w-4" /> Escrevendo o rascunho para o cliente…
            </div>
          ) : done ? (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">Publicado no portal do cliente.</p>
          ) : (
            <>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Título"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-medium focus:border-brand-500 focus:outline-none"
              />
              <div className="flex gap-1 text-xs">
                <button type="button" onClick={() => setPreview(false)} className={`rounded px-2 py-1 ${!preview ? 'bg-slate-900 text-white' : 'bg-slate-100'}`}>
                  Editar
                </button>
                <button type="button" onClick={() => setPreview(true)} className={`rounded px-2 py-1 ${preview ? 'bg-slate-900 text-white' : 'bg-slate-100'}`}>
                  Ver como o cliente
                </button>
              </div>
              {preview ? (
                <div className="markdown rounded-md border border-slate-200 p-3">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={16}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
                />
              )}
            </>
          )}
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
            {done ? 'Fechar' : 'Cancelar'}
          </button>
          {!done && (
            <button
              type="button"
              onClick={() => onPublish(title.trim(), content.trim())}
              disabled={publishing || loadingDraft || !title.trim() || content.trim().length < 20}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {publishing && <Spinner className="h-4 w-4 text-white" />}
              Publicar para o cliente
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
